import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Modal,
  Spin,
  Tag,
  Typography,
} from 'antd';
import {
  AppstoreOutlined,
  BranchesOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  FieldTimeOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  ReloadOutlined,
  RightOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { sanitizeHTML } from '../../utils';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/en';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Title, Text } = Typography;

interface CalendarEvent {
  id: string;
  title: string;
  start: string; // ISO string
  end: string; // ISO string
  type?: string;
  color?: string;
  description?: string;
  location?: string;
  source?: string;
  all_day?: boolean;
  attendees?: string[];
  status?: string;
  recurring?: boolean;
  metadata?: Record<string, unknown>;
  /** When two API rows were merged (same title + time), each source’s accent color for split styling */
  merged_colors?: string[];
  [key: string]: unknown;
}

interface CalendarPageProps {
  agentId: string;
  apiUrl: string;
  accessToken: string;
}

/** API `items[]` element; unknown fields are preserved via spread onto `CalendarEvent`. */
interface FetchedCalendarItem {
  id: string;
  title?: string;
  start_time?: string;
  end_time?: string;
  type?: string;
  color?: string;
  description?: string | null;
  location?: string | null;
  source?: string;
  all_day?: boolean;
  attendees?: string[];
  status?: string;
  recurring?: boolean;
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
}

const eventTypeColor: { [key: string]: string } = {
  meeting: 'blue',
  followup: 'green',
  other: 'orange',
};

const statusColor: { [key: string]: string } = {
  confirmed: 'green',
  cancelled: 'red',
  tentative: 'orange',
  completed: 'blue',
};

/** Single place for calendar source colors (legend tags + event fills + modal provider tags). */
const CALENDAR_INTEGRATION_STYLES: Record<
  string,
  { tagColor: string; hex: string }
> = {
  google: { tagColor: 'green', hex: '#4caf50' },
  azure: { tagColor: 'orange', hex: '#ff9800' },
  crm: { tagColor: 'blue', hex: '#1976d2' },
  fub: { tagColor: 'blue', hex: '#1976d2' },
};

const DEFAULT_CALENDAR_INTEGRATION = {
  tagColor: 'default',
  hex: '#1890ff',
} as const;

/** Keys shown in the “Sources” legend (FUB uses the same styling as CRM). */
const CALENDAR_LEGEND_SOURCE_KEYS = ['google', 'azure', 'crm'] as const;

function getCalendarIntegrationStyle(sourceKey: string) {
  const style = CALENDAR_INTEGRATION_STYLES[sourceKey.toLowerCase()];
  return style ?? DEFAULT_CALENDAR_INTEGRATION;
}

function parseEventSources(source?: string): string[] {
  if (!source?.trim()) {
    return [];
  }
  const parts = source
    .split(',')
    .map(s => s.trim().toLowerCase())
    .filter(Boolean);
  return Array.from(new Set(parts));
}

function resolveIntegrationHexColor(sourceKey: string): string {
  return getCalendarIntegrationStyle(sourceKey).hex;
}

function resolveEventAccentColor(event: CalendarEvent): string {
  if (event.color) {
    return event.color;
  }
  const first = parseEventSources(event.source)[0];
  return first ? resolveIntegrationHexColor(first) : '#1890ff';
}

function eventIsMergedFromMultipleSources(event: CalendarEvent): boolean {
  return parseEventSources(event.source).length > 1;
}

function buildMultiSourceBackground(colors: string[]): string {
  const unique = Array.from(new Set(colors.filter(Boolean)));
  if (unique.length === 0) {
    return '#1890ff';
  }
  if (unique.length === 1) {
    return unique[0];
  }
  const n = unique.length;
  const segments = unique.map((c, i) => {
    const start = (i / n) * 100;
    const end = ((i + 1) / n) * 100;
    return `${c} ${start}% ${end}%`;
  });
  return `linear-gradient(135deg, ${segments.join(', ')})`;
}

type EventSurfaceStyle = {
  background: string;
  borderLeftColor: string;
  textShadow?: string;
};

function eventIsCancelled(event: CalendarEvent): boolean {
  const s = event.status?.toLowerCase();
  return (
    s === 'cancelled' ||
    s === 'canceled' ||
    Boolean(event.title?.toLowerCase().includes('cancel'))
  );
}

function getEventSurfaceStyle(
  event: CalendarEvent,
  isCancelled: boolean,
): EventSurfaceStyle {
  if (isCancelled) {
    return {
      background: '#f5f5f5',
      borderLeftColor: '#d9d9d9',
    };
  }
  const merged = eventIsMergedFromMultipleSources(event);
  let palette: string[] = [];
  if (event.merged_colors?.length) {
    palette = Array.from(new Set(event.merged_colors.filter(Boolean)));
  } else if (merged) {
    palette = Array.from(
      new Set(parseEventSources(event.source).map(resolveIntegrationHexColor)),
    );
  }

  if (merged && palette.length >= 2) {
    return {
      background: buildMultiSourceBackground(palette),
      borderLeftColor: palette[0],
      textShadow: '0 1px 2px rgba(0,0,0,0.35)',
    };
  }

  const solid =
    event.color ||
    palette[0] ||
    resolveIntegrationHexColor(parseEventSources(event.source)[0] || '') ||
    '#1890ff';
  return {
    background: solid,
    borderLeftColor: solid,
  };
}

function tagColorForSource(sourceKey: string): string {
  return getCalendarIntegrationStyle(sourceKey).tagColor;
}

function renderProviderTags(source: string | undefined): React.ReactNode {
  const sources = parseEventSources(source);
  if (sources.length === 0) {
    return source?.trim() || '—';
  }
  return sources.map(s => (
    <Tag color={tagColorForSource(s)} key={s}>
      {formatSourceLabel(s)}
    </Tag>
  ));
}

function formatSourceLabel(sourceKey: string): string {
  const k = sourceKey.toLowerCase();
  if (k === 'fub') {
    return 'CRM (FUB)';
  }
  return sourceKey.charAt(0).toUpperCase() + sourceKey.slice(1).toLowerCase();
}

const timeSlots = [
  '12:00 AM',
  '1:00 AM',
  '2:00 AM',
  '3:00 AM',
  '4:00 AM',
  '5:00 AM',
  '6:00 AM',
  '7:00 AM',
  '8:00 AM',
  '9:00 AM',
  '10:00 AM',
  '11:00 AM',
  '12:00 PM',
  '1:00 PM',
  '2:00 PM',
  '3:00 PM',
  '4:00 PM',
  '5:00 PM',
  '6:00 PM',
  '7:00 PM',
  '8:00 PM',
  '9:00 PM',
  '10:00 PM',
  '11:00 PM',
];

const CalendarPage: React.FC<CalendarPageProps> = ({
  agentId,
  apiUrl,
  accessToken,
}) => {
  const [currentWeek, setCurrentWeek] = useState(dayjs().startOf('week'));
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(
    null,
  );
  const [agentTimezone, setAgentTimezone] = useState<string | null>(null);

  const fetchEvents = useCallback(
    async (start: Dayjs, end: Dayjs) => {
      setLoading(true);
      setEvents([]);
      try {
        const params = new URLSearchParams({
          agent_id: agentId,
          start_date: start.format('YYYY-MM-DD'),
          end_date: end.format('YYYY-MM-DD'),
        });
        const response = await fetch(
          `${apiUrl}/api/admin/calendar/fetch?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        if (data.success && Array.isArray(data.items)) {
          // Store agent timezone from API response
          if (data.agent_info?.timezone) {
            setAgentTimezone(data.agent_info.timezone);
          }

          // Map items to events
          const mappedEvents: CalendarEvent[] = (
            data.items as FetchedCalendarItem[]
          ).map(item => ({
            ...item,
            title: item.title ?? '',
            start: item.start_time ?? '',
            end: item.end_time ?? '',
            description: item.description ?? undefined,
            location: item.location ?? undefined,
          }));

          const agentTz = data.agent_info?.timezone || null;
          const eventMap = new Map<string, CalendarEvent>();

          mappedEvents.forEach((event: CalendarEvent) => {
            let normalizedStart = event.start;
            let normalizedEnd = event.end;

            if (agentTz) {
              try {
                const startParsed = dayjs(event.start);
                const endParsed = dayjs(event.end);
                if (startParsed.isValid() && endParsed.isValid()) {
                  normalizedStart = startParsed
                    .tz(agentTz)
                    .format('YYYY-MM-DDTHH:mm:ss');
                  normalizedEnd = endParsed
                    .tz(agentTz)
                    .format('YYYY-MM-DDTHH:mm:ss');
                }
              } catch (e) {
                // If timezone conversion fails, use original values
                console.warn(
                  'Failed to normalize event time for deduplication:',
                  e,
                );
              }
            }

            const normalizedTitle = (event.title || '').trim().toLowerCase();
            const key = `${normalizedTitle}|${normalizedStart}|${normalizedEnd}`;

            if (eventMap.has(key)) {
              // Event already exists, merge attendees and other properties
              const existingEvent = eventMap.get(key)!;

              // Merge attendees (combine unique attendees from all sources)
              const allAttendees = [
                ...(existingEvent.attendees || []),
                ...(event.attendees || []),
              ];
              const uniqueAttendees = Array.from(
                new Set(allAttendees.filter(Boolean)),
              );

              // Determine which sources are combined (for display purposes)
              const sources = new Set<string>();
              if (existingEvent.source) {
                sources.add(existingEvent.source);
              }
              if (event.source) {
                sources.add(event.source);
              }
              const combinedSource = Array.from(sources).join(', ');

              const prevColors = existingEvent.merged_colors?.length
                ? [...existingEvent.merged_colors]
                : [resolveEventAccentColor(existingEvent)];
              const nextColor = resolveEventAccentColor(event);
              const merged_colors = [...prevColors, nextColor];

              // Keep the event with more complete information
              const mergedEvent: CalendarEvent = {
                ...existingEvent,
                attendees:
                  uniqueAttendees.length > 0
                    ? uniqueAttendees
                    : existingEvent.attendees,
                // Prefer ID from calendar events over CRM events (calendar IDs are more specific)
                id: existingEvent.id?.startsWith('cal_')
                  ? existingEvent.id
                  : event.id?.startsWith('cal_')
                    ? event.id
                    : existingEvent.id || event.id,
                // Merge descriptions and locations (prefer non-empty, longer values)
                description:
                  (existingEvent.description || '').length >
                  (event.description || '').length
                    ? existingEvent.description
                    : event.description || existingEvent.description,
                location:
                  (existingEvent.location || '').length >
                  (event.location || '').length
                    ? existingEvent.location
                    : event.location || existingEvent.location,
                status:
                  existingEvent.status === 'confirmed'
                    ? existingEvent.status
                    : event.status || existingEvent.status,
                source: combinedSource || existingEvent.source || event.source,
                metadata: existingEvent.metadata || event.metadata,
                merged_colors,
              };

              eventMap.set(key, mergedEvent);
            } else {
              eventMap.set(key, event);
            }
          });

          setEvents(Array.from(eventMap.values()));
        } else {
          setEvents([]);
        }
      } catch (e) {
        console.error('Error fetching calendar events:', e);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    },
    [agentId, apiUrl, accessToken],
  );

  useEffect(() => {
    if (!agentId || !accessToken) {
      return;
    }

    const startDate = currentWeek.startOf('week');
    const endDate = currentWeek.endOf('week');

    void fetchEvents(startDate, endDate);
  }, [currentWeek, fetchEvents, agentId, accessToken]);

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setModalOpen(true);
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    setCurrentWeek(prev =>
      direction === 'prev' ? prev.subtract(1, 'week') : prev.add(1, 'week'),
    );
  };

  const getEventPosition = (event: CalendarEvent, day: Dayjs) => {
    if (!agentTimezone) {
      return null;
    }

    const eventStart = dayjs(event.start).tz(agentTimezone);
    const eventEnd = dayjs(event.end).tz(agentTimezone);
    const dayStr = day.format('YYYY-MM-DD');
    const eventStartDay = eventStart.format('YYYY-MM-DD');

    if (
      eventStartDay !== dayStr ||
      !eventStart.isValid() ||
      !eventEnd.isValid()
    ) {
      return null;
    }

    const startOfDay = dayjs.tz(
      `${dayStr} 00:00:00`,
      'YYYY-MM-DD HH:mm:ss',
      agentTimezone,
    );
    const minutesFromStart = eventStart.diff(startOfDay, 'minute');
    const duration = eventEnd.diff(eventStart, 'minute');

    const topPercent = (minutesFromStart / (24 * 60)) * 100;
    const heightPercent = (duration / (24 * 60)) * 100;

    const minHeight = Math.max(heightPercent, 1.0);

    return {
      top: topPercent,
      height: minHeight,
      startTime: eventStart,
      endTime: eventEnd,
      startMinutes: minutesFromStart,
      endMinutes: minutesFromStart + duration,
    };
  };

  const eventsOverlap = (
    pos1: ReturnType<typeof getEventPosition>,
    pos2: ReturnType<typeof getEventPosition>,
  ) => {
    if (!pos1 || !pos2) {
      return false;
    }
    return !(
      pos1.endMinutes <= pos2.startMinutes ||
      pos2.endMinutes <= pos1.startMinutes
    );
  };

  const BASE_DAY_HEIGHT = 24 * 45;

  const calculateEventLayout = (events: CalendarEvent[], day: Dayjs) => {
    if (!agentTimezone) {
      return { layout: [], columnHeight: BASE_DAY_HEIGHT };
    }

    const eventPositions = events
      .map(event => {
        const pos = getEventPosition(event, day);
        return pos ? { event, position: pos } : null;
      })
      .filter(
        (
          item,
        ): item is {
          event: CalendarEvent;
          position: NonNullable<ReturnType<typeof getEventPosition>>;
        } => item !== null && item.position !== null,
      )
      .sort((a, b) => a.position.startMinutes - b.position.startMinutes);

    const groups: Array<
      Array<{
        event: CalendarEvent;
        position: NonNullable<ReturnType<typeof getEventPosition>>;
      }>
    > = [];

    eventPositions.forEach(item => {
      let added = false;
      for (const group of groups) {
        if (
          group.some(groupItem =>
            eventsOverlap(item.position, groupItem.position),
          )
        ) {
          group.push(item);
          added = true;
          break;
        }
      }
      if (!added) {
        groups.push([item]);
      }
    });

    const layout: Array<{
      event: CalendarEvent;
      position: NonNullable<ReturnType<typeof getEventPosition>>;
      left: number;
      width: number;
      topPx: number;
      heightPx: number;
    }> = [];

    groups.forEach(group => {
      group.forEach((item, groupIndex) => {
        const topPx = (item.position.top / 100) * BASE_DAY_HEIGHT;
        let heightPx = (item.position.height / 100) * BASE_DAY_HEIGHT;
        const minHeightPx = 14;
        heightPx = Math.max(heightPx, minHeightPx);

        const width = group.length > 1 ? 100 / group.length : 100;
        const left = group.length > 1 ? groupIndex * width : 0;

        layout.push({
          ...item,
          position: {
            ...item.position,
            top: (topPx / BASE_DAY_HEIGHT) * 100,
            height: (heightPx / BASE_DAY_HEIGHT) * 100,
          },
          left: left,
          width: width,
          topPx: topPx,
          heightPx: heightPx,
        });
      });
    });

    let maxBottomPx = BASE_DAY_HEIGHT;
    if (eventPositions.length > 0) {
      eventPositions.forEach(item => {
        const endPx =
          (item.position.top / 100) * BASE_DAY_HEIGHT +
          Math.max((item.position.height / 100) * BASE_DAY_HEIGHT, 14);
        maxBottomPx = Math.max(maxBottomPx, endPx);
      });
      maxBottomPx += 10;
    }

    return { layout, columnHeight: maxBottomPx };
  };

  const getEventsForDay = (day: Dayjs) => {
    if (!agentTimezone) {
      return { timed: [], allDay: [] };
    }

    const dayStr = day.format('YYYY-MM-DD');
    const dayEvents = events.filter(event => {
      const eventStart = dayjs(event.start).tz(agentTimezone);
      if (!eventStart.isValid()) {
        return false;
      }
      return eventStart.format('YYYY-MM-DD') === dayStr;
    });

    const timed = dayEvents.filter(event => !event.all_day);
    const allDay = dayEvents.filter(event => event.all_day);

    return { timed, allDay };
  };

  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(currentWeek.add(i, 'day'));
    }
    return days;
  };

  const renderMetadata = (metadata: Record<string, unknown> | undefined) => {
    if (!metadata) {
      return null;
    }
    return (
      <Descriptions
        bordered
        column={1}
        size="small"
        style={{ marginTop: 12, background: '#fafcff', borderRadius: 8 }}
        title={
          <span>
            <AppstoreOutlined /> Metadata
          </span>
        }
      >
        {Object.entries(metadata).map(([key, value]) => (
          <Descriptions.Item key={key} label={key}>
            {String(value)}
          </Descriptions.Item>
        ))}
      </Descriptions>
    );
  };

  const weekDays = getWeekDays();

  return (
    <div style={{ minHeight: '80vh', padding: '20px', background: '#f5f5f5' }}>
      <Card
        bodyStyle={{ padding: 0 }}
        style={{
          width: '100%',
          margin: '0 auto',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: '8px',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 24px',
            borderBottom: '1px solid #e8e8e8',
            background: '#fff',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <CalendarOutlined style={{ fontSize: 20, color: '#1890ff' }} />
            <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
              Calendar
            </Title>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Button
              icon={<LeftOutlined />}
              onClick={() => navigateWeek('prev')}
              size="small"
              style={{ border: '1px solid #d9d9d9' }}
            />
            <Text
              strong
              style={{ minWidth: 200, textAlign: 'center', fontSize: 15 }}
            >
              {currentWeek.format('MMM D')} -{' '}
              {currentWeek.add(6, 'day').format('MMM D, YYYY')}
            </Text>
            <Button
              icon={<RightOutlined />}
              onClick={() => navigateWeek('next')}
              size="small"
              style={{ border: '1px solid #d9d9d9' }}
            />
            <Button
              icon={<ReloadOutlined />}
              loading={loading}
              onClick={() => {
                const startDate = currentWeek.startOf('week');
                const endDate = currentWeek.endOf('week');
                void fetchEvents(startDate, endDate);
              }}
              style={{ marginLeft: 8 }}
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Integration Color Key */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
            padding: '12px 24px',
            background: '#fafafa',
            borderBottom: '1px solid #e8e8e8',
          }}
        >
          <span style={{ fontWeight: 500, fontSize: 13, color: '#666' }}>
            Sources:
          </span>
          <Text style={{ fontSize: 12, marginRight: 4 }} type="secondary">
            <BranchesOutlined style={{ marginRight: 4 }} />
            Split colors / branches icon = same slot merged from multiple
            sources.
          </Text>
          {CALENDAR_LEGEND_SOURCE_KEYS.map(sourceKey => {
            const { tagColor } = getCalendarIntegrationStyle(sourceKey);
            return (
              <Tag
                color={tagColor}
                key={sourceKey}
                style={{
                  fontSize: 12,
                  padding: '2px 10px',
                  margin: 0,
                  borderRadius: '4px',
                  border: 'none',
                }}
              >
                {sourceKey.charAt(0).toUpperCase() + sourceKey.slice(1)}
              </Tag>
            );
          })}
        </div>

        <Spin spinning={loading}>
          <div style={{ overflowX: 'auto', background: '#fff' }}>
            <div style={{ minWidth: 1200 }}>
              {/* All-day events row */}
              {(() => {
                const maxAllDayCount = Math.max(
                  ...weekDays.map(day => getEventsForDay(day).allDay.length),
                  0,
                );
                const rowHeight = Math.max(32, maxAllDayCount * 24 + 6);

                return (
                  <div
                    style={{
                      display: 'flex',
                      borderBottom: '2px solid #e8e8e8',
                      background: '#fff',
                      minHeight: `${rowHeight}px`,
                    }}
                  >
                    <div
                      style={{
                        width: '80px',
                        minWidth: '80px',
                        padding: '8px',
                        textAlign: 'center',
                        fontWeight: 600,
                        fontSize: 11,
                        color: '#666',
                        borderRight: '1px solid #e8e8e8',
                        background: '#fafafa',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        paddingTop: '10px',
                      }}
                    >
                      All Day
                    </div>
                    {weekDays.map((day, dayIndex) => {
                      const isToday = day.isSame(dayjs(), 'day');
                      const { allDay } = getEventsForDay(day);

                      return (
                        <div
                          key={dayIndex}
                          style={{
                            flex: 1,
                            padding: '4px 2px',
                            borderLeft:
                              dayIndex > 0 ? '1px solid #e8e8e8' : 'none',
                            background: isToday ? '#fafcff' : '#fff',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                          }}
                        >
                          {allDay.map(event => {
                            const isCancelled = eventIsCancelled(event);
                            const surface = getEventSurfaceStyle(
                              event,
                              isCancelled,
                            );
                            const merged =
                              eventIsMergedFromMultipleSources(event);

                            return (
                              <div
                                key={event.id}
                                onClick={() => handleEventClick(event)}
                                onMouseEnter={e => {
                                  e.currentTarget.style.boxShadow =
                                    '0 2px 4px rgba(0,0,0,0.15)';
                                }}
                                onMouseLeave={e => {
                                  e.currentTarget.style.boxShadow =
                                    '0 1px 2px rgba(0,0,0,0.1)';
                                }}
                                style={{
                                  background: surface.background,
                                  color: isCancelled ? '#8c8c8c' : '#fff',
                                  padding: '3px 6px',
                                  borderRadius: '3px',
                                  cursor: 'pointer',
                                  fontSize: 11,
                                  fontWeight: 500,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  borderLeft: `3px solid ${surface.borderLeftColor}`,
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                  transition: 'all 0.2s ease',
                                  textShadow: surface.textShadow,
                                }}
                                title={
                                  merged
                                    ? `${event.title} (merged from multiple sources)`
                                    : event.title
                                }
                              >
                                {isCancelled ? (
                                  <span
                                    style={{ textDecoration: 'line-through' }}
                                  >
                                    {event.title}
                                  </span>
                                ) : (
                                  <>
                                    {merged && (
                                      <BranchesOutlined
                                        style={{
                                          marginRight: 4,
                                          opacity: 0.95,
                                        }}
                                      />
                                    )}
                                    {event.title}
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Header row with day names */}
              <div
                style={{
                  display: 'flex',
                  borderBottom: '1px solid #e8e8e8',
                  background: '#fff',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                }}
              >
                <div
                  style={{
                    width: '80px',
                    minWidth: '80px',
                    padding: '12px 8px',
                    textAlign: 'center',
                    fontWeight: 600,
                    fontSize: 12,
                    color: '#666',
                    borderRight: '1px solid #e8e8e8',
                    background: '#fafafa',
                  }}
                />
                {weekDays.map((day, index) => {
                  const isToday = day.isSame(dayjs(), 'day');
                  return (
                    <div
                      key={index}
                      style={{
                        flex: 1,
                        padding: '12px 8px',
                        textAlign: 'center',
                        fontWeight: 600,
                        borderLeft: index > 0 ? '1px solid #e8e8e8' : 'none',
                        background: isToday ? '#e6f7ff' : '#fff',
                        position: 'relative',
                      }}
                    >
                      <div
                        style={{ fontSize: 12, color: '#666', marginBottom: 4 }}
                      >
                        {day.format('ddd').toUpperCase()}
                      </div>
                      <div
                        style={{
                          fontSize: 18,
                          color: isToday ? '#1890ff' : '#262626',
                          fontWeight: isToday ? 700 : 600,
                        }}
                      >
                        {day.format('D')}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Calendar Grid */}
              {(() => {
                const dayLayouts = weekDays.map(day => {
                  const { timed } = getEventsForDay(day);
                  return calculateEventLayout(timed, day);
                });

                const maxColumnHeight = Math.max(
                  ...dayLayouts.map(layout => layout.columnHeight),
                  BASE_DAY_HEIGHT,
                );

                return (
                  <div style={{ display: 'flex', position: 'relative' }}>
                    {/* Time column */}
                    <div
                      style={{
                        width: '80px',
                        minWidth: '80px',
                        borderRight: '1px solid #e8e8e8',
                        background: '#fafafa',
                        height: `${maxColumnHeight}px`,
                      }}
                    >
                      {timeSlots.map((timeSlot, timeIndex) => (
                        <div
                          key={timeIndex}
                          style={{
                            height: '45px',
                            padding: '2px 8px',
                            textAlign: 'right',
                            fontSize: 10,
                            color: '#8c8c8c',
                            borderBottom: '1px solid #f0f0f0',
                            position: 'relative',
                          }}
                        >
                          {timeSlot}
                        </div>
                      ))}
                      {maxColumnHeight > BASE_DAY_HEIGHT && (
                        <div
                          style={{
                            height: `${maxColumnHeight - BASE_DAY_HEIGHT}px`,
                            borderTop: '1px solid #f0f0f0',
                          }}
                        />
                      )}
                    </div>

                    {/* Day columns */}
                    {weekDays.map((day, dayIndex) => {
                      const isToday = day.isSame(dayjs(), 'day');
                      const { layout: eventLayout, columnHeight } =
                        dayLayouts[dayIndex];

                      return (
                        <div
                          key={dayIndex}
                          style={{
                            flex: 1,
                            borderLeft:
                              dayIndex > 0 ? '1px solid #e8e8e8' : 'none',
                            position: 'relative',
                            background: isToday ? '#fafcff' : '#fff',
                            height: `${columnHeight}px`,
                            minHeight: `${BASE_DAY_HEIGHT}px`,
                          }}
                        >
                          {/* Today indicator line */}
                          {isToday && (
                            <div
                              style={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: '3px',
                                background: '#1890ff',
                                zIndex: 5,
                              }}
                            />
                          )}

                          {/* Hour grid lines */}
                          {timeSlots.map((_, timeIndex) => (
                            <div
                              key={timeIndex}
                              style={{
                                height: '45px',
                                borderBottom: '1px solid #f0f0f0',
                                position: 'relative',
                              }}
                            />
                          ))}

                          {/* Additional grid lines if column is taller */}
                          {columnHeight > BASE_DAY_HEIGHT && (
                            <div
                              style={{
                                height: `${columnHeight - BASE_DAY_HEIGHT}px`,
                                borderTop: '1px solid #f0f0f0',
                                position: 'relative',
                              }}
                            />
                          )}

                          {/* Events */}
                          <div
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              padding: '2px',
                            }}
                          >
                            {eventLayout.map(
                              ({
                                event,
                                position,
                                left,
                                width,
                                topPx,
                                heightPx,
                              }) => {
                                const isCancelled = eventIsCancelled(event);
                                const surface = getEventSurfaceStyle(
                                  event,
                                  isCancelled,
                                );
                                const merged =
                                  eventIsMergedFromMultipleSources(event);

                                return (
                                  <div
                                    key={event.id}
                                    onClick={() => handleEventClick(event)}
                                    onMouseEnter={e => {
                                      e.currentTarget.style.boxShadow =
                                        '0 2px 8px rgba(0,0,0,0.2)';
                                      e.currentTarget.style.transform =
                                        'scale(1.01)';
                                      e.currentTarget.style.zIndex = '10';
                                    }}
                                    onMouseLeave={e => {
                                      e.currentTarget.style.boxShadow =
                                        '0 1px 3px rgba(0,0,0,0.12)';
                                      e.currentTarget.style.transform =
                                        'scale(1)';
                                      e.currentTarget.style.zIndex = '1';
                                    }}
                                    style={{
                                      position: 'absolute',
                                      top: `${topPx}px`,
                                      left: left > 0 ? `${left}%` : '2px',
                                      right:
                                        left + width < 100
                                          ? `${100 - (left + width)}%`
                                          : '2px',
                                      width:
                                        left > 0 || left + width < 100
                                          ? `${width}%`
                                          : 'auto',
                                      marginLeft: left > 0 ? '2px' : '0',
                                      marginRight:
                                        left + width < 100 ? '2px' : '0',
                                      height: `${heightPx}px`,
                                      minHeight: '14px',
                                      background: surface.background,
                                      color: isCancelled ? '#8c8c8c' : '#fff',
                                      padding: '2px 6px',
                                      borderRadius: '3px',
                                      cursor: 'pointer',
                                      overflow: 'hidden',
                                      borderLeft: `3px solid ${surface.borderLeftColor}`,
                                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                      transition: 'all 0.2s ease',
                                      zIndex: 1,
                                      textShadow: surface.textShadow,
                                    }}
                                    title={`${event.title} - ${position.startTime.format('h:mm A')} - ${position.endTime.format('h:mm A')}${merged ? ' — merged from multiple sources' : ''}`}
                                  >
                                    <div
                                      style={{
                                        fontSize: 10,
                                        fontWeight: 600,
                                        lineHeight: 1.3,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: Math.max(
                                          1,
                                          Math.floor(position.height / 1.0),
                                        ),
                                        WebkitBoxOrient: 'vertical',
                                      }}
                                    >
                                      {isCancelled && (
                                        <span
                                          style={{
                                            textDecoration: 'line-through',
                                          }}
                                        >
                                          {event.title}
                                        </span>
                                      )}
                                      {!isCancelled && merged && (
                                        <BranchesOutlined
                                          style={{
                                            marginRight: 3,
                                            opacity: 0.95,
                                            fontSize: 9,
                                          }}
                                        />
                                      )}
                                      {!isCancelled && event.title}
                                    </div>
                                    {position.height > 2.0 && (
                                      <div
                                        style={{
                                          fontSize: 9,
                                          opacity: 0.9,
                                          marginTop: 1,
                                          fontWeight: 500,
                                        }}
                                      >
                                        {position.startTime.format('h:mm A')}
                                      </div>
                                    )}
                                  </div>
                                );
                              },
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        </Spin>

        <Modal
          bodyStyle={{ background: '#fafdff', borderRadius: 12 }}
          footer={null}
          onCancel={() => setModalOpen(false)}
          open={modalOpen}
          title={
            selectedEvent ? (
              <>
                <CalendarOutlined /> {selectedEvent.title}
              </>
            ) : (
              'Event Details'
            )
          }
          width={650}
        >
          {selectedEvent && (
            <div style={{ padding: 18, background: '#fff', borderRadius: 8 }}>
              {eventIsMergedFromMultipleSources(selectedEvent) && (
                <Alert
                  description="The same title and time appeared in more than one feed. This view combines fields from each; colors in the grid reflect each source."
                  icon={<BranchesOutlined />}
                  message="Merged from multiple sources"
                  showIcon
                  style={{ marginBottom: 16 }}
                  type="info"
                />
              )}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
              >
                <Tag
                  color={
                    selectedEvent.color ||
                    eventTypeColor[selectedEvent.type || 'other'] ||
                    '#1890ff'
                  }
                  style={{ fontWeight: 600, fontSize: 15, marginRight: 8 }}
                >
                  {selectedEvent.type || 'Event'}
                </Tag>
                <span style={{ fontWeight: 600, fontSize: 18 }}>
                  {selectedEvent.title}
                </span>
                {selectedEvent.status && (
                  <Tag
                    color={
                      statusColor[selectedEvent.status.toLowerCase()] ||
                      'default'
                    }
                    style={{ marginLeft: 12 }}
                  >
                    {selectedEvent.status}
                  </Tag>
                )}
              </div>
              <Descriptions
                bordered
                column={1}
                size="small"
                style={{ background: '#fafdff', borderRadius: 8 }}
              >
                <Descriptions.Item
                  label={
                    <>
                      <FieldTimeOutlined /> Start
                    </>
                  }
                >
                  {agentTimezone
                    ? dayjs(selectedEvent.start)
                        .tz(agentTimezone)
                        .format('YYYY-MM-DD h:mm A')
                    : dayjs(selectedEvent.start).format('YYYY-MM-DD h:mm A')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <>
                      <FieldTimeOutlined /> End
                    </>
                  }
                >
                  {agentTimezone
                    ? dayjs(selectedEvent.end)
                        .tz(agentTimezone)
                        .format('YYYY-MM-DD h:mm A')
                    : dayjs(selectedEvent.end).format('YYYY-MM-DD h:mm A')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <>
                      <InfoCircleOutlined /> ID
                    </>
                  }
                >
                  {selectedEvent.id}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <>
                      <AppstoreOutlined /> Provider
                    </>
                  }
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      flexWrap: 'wrap',
                      gap: 6,
                      alignItems: 'center',
                    }}
                  >
                    {renderProviderTags(selectedEvent.source)}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <>
                      <InfoCircleOutlined /> Recurring
                    </>
                  }
                >
                  {String(selectedEvent.recurring)}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <>
                      <InfoCircleOutlined /> All Day
                    </>
                  }
                >
                  {String(selectedEvent.all_day)}
                </Descriptions.Item>
                {selectedEvent.location && selectedEvent.location.trim() && (
                  <Descriptions.Item
                    label={
                      <>
                        <EnvironmentOutlined /> Location
                      </>
                    }
                  >
                    {selectedEvent.location}
                  </Descriptions.Item>
                )}
                {selectedEvent.description && (
                  <Descriptions.Item
                    label={
                      <>
                        <InfoCircleOutlined /> Description
                      </>
                    }
                  >
                    <div
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHTML(selectedEvent.description),
                      }}
                      style={{
                        maxHeight: '200px',
                        overflowY: 'auto',
                        lineHeight: '1.5',
                      }}
                    />
                  </Descriptions.Item>
                )}
                {selectedEvent.attendees &&
                  Array.isArray(selectedEvent.attendees) &&
                  selectedEvent.attendees.length > 0 && (
                    <Descriptions.Item
                      label={
                        <>
                          <TeamOutlined /> Attendees
                        </>
                      }
                    >
                      {selectedEvent.attendees.join(', ')}
                    </Descriptions.Item>
                  )}
              </Descriptions>
              {renderMetadata(selectedEvent.metadata)}
            </div>
          )}
        </Modal>
      </Card>
    </div>
  );
};

export default CalendarPage;
